import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  Alert,
  Collapse,
  Container,
  Stack,
} from "@mui/material";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import ThinkingLoader from "./ThinkingLoader";
import { useTranslation } from "react-i18next";

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatInterfaceProps {
  fullPage?: boolean;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ fullPage = false }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastMessageIsUser, setLastMessageIsUser] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const [isCurrentlyTyping, setIsCurrentlyTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const { t } = useTranslation();

  // Suggested questions for the user to ask
  const suggestedQuestions = [
    t("What projects has Shoeb worked on?"),
    t("What are Shoeb's technical skills?"),
    t("What is Shoeb's work experience?"),
    t("Tell me about Shoeb's educational background"),
    t("Which technologies do you use most?"),
    t("What are your career highlights?"),
  ];

  // Handle typing state change from ChatMessage component
  const handleTypingStateChange = (isTyping: boolean) => {
    setIsCurrentlyTyping(isTyping);
  };

  // Track if the last message was from the user
  useEffect(() => {
    if (messages.length > 0) {
      setLastMessageIsUser(messages[messages.length - 1].isUser);
      setInitialLoad(false);
    }
  }, [messages]);

  // Remove the welcome message effect as we want to directly show user's message
  // useEffect(() => {
  //   if (hasUserSentMessage && messages.length === 1) {
  //     // Add a small timeout to ensure DOM is ready before adding welcome message
  //     const timer = setTimeout(() => {
  //       setMessages([
  //         {
  //           id: "welcome",
  //           content: t("Hi there! I'm Shoeb's portfolio assistant. How can I help you today?"),
  //           isUser: false,
  //           timestamp: new Date(),
  //         },
  //       ]);
  //     }, 100);
      
  //     return () => clearTimeout(timer);
  //   }
  // }, [hasUserSentMessage, messages.length, t]);

  // Scroll handling - adjusted to prevent auto-scrolling on initial load
  useEffect(() => {
    // Only auto-scroll if not initial load or if there's more than one message
    if (!initialLoad || messages.length > 0) {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    } else if (messageContainerRef.current && initialLoad) {
      // Reset scroll position on initial load
      messageContainerRef.current.scrollTop = 0;
    }
  }, [messages, initialLoad]);

  // Reset scroll position on component mount
  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = 0;
    }
  }, []);

  const handleSendMessage = async (content: string) => {
    // If this is the first user message, set the flag
    if (!hasUserSentMessage) {
      setHasUserSentMessage(true);
    }
    
    // Create a new user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      content,
      isUser: true,
      timestamp: new Date(),
    };

    // Add the user message to the chat
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      // Send the message to the chat server using environment variable
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: content,
          conversationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response from server");
      }

      const data = await response.json();
      
      // Save the conversation ID if we don't have one yet
      if (!conversationId && data.conversationId) {
        setConversationId(data.conversationId);
      }

      // Add the assistant's response to the chat
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: `assistant-${Date.now()}`,
          content: data.response,
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error("Error communicating with chat server:", error);
      
      // Set error state
      setError(error instanceof Error ? error.message : "An unknown error occurred");
      
      // Add an error message
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: `error-${Date.now()}`,
          content: t("Sorry, I'm having trouble connecting to the server. Please check if the server is running and try again."),
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSendMessage(question);
  };

  // Function to get random suggested questions
  const getRandomSuggestions = (count = 4) => {
    // Shuffle array and take first 'count' items
    return [...suggestedQuestions]
      .sort(() => 0.5 - Math.random())
      .slice(0, count);
  };

  // Component for rendering suggested questions
  const SuggestedQuestions = () => {
    // Show random suggestions
    const randomSuggestions = getRandomSuggestions();
    
    return (
      <Box sx={{ 
        my: 3, 
        textAlign: "center",
        px: 2
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("You can ask me about:")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "center",
            mb: 4,
          }}
        >
          {randomSuggestions.map((question, index) => (
              <Paper
                elevation={0}
                onClick={() => handleSuggestedQuestion(question)}
                sx={{
                  p: 1.5,
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(52, 53, 65, 0.7)' 
                    : 'rgba(247, 247, 248, 0.9)',
                  borderRadius: "1rem",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  border: `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(52, 53, 65, 0.9)'
                      : 'rgba(247, 247, 248, 1)',
                    boxShadow: theme.shadows[2],
                  },
                  transition: theme.transitions.create(['background-color', 'box-shadow'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  maxWidth: '240px',
                }}
              >
                <Typography variant="body2" noWrap>
                  {question}
                </Typography>
              </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  // Initial Screen Component - shown when user first opens the chat
  const InitialScreen = () => {
    const randomSuggestions = getRandomSuggestions(4);
    
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Stack 
          direction="column" 
          spacing={4}
          alignItems="center"
          justifyContent="center"
          sx={{ 
            maxWidth: "650px",
            width: "100%",
            mx: "auto",
            px: 2,
            py: { xs: 4, sm: 2 },
          }}
        >
          <Typography 
            variant="h4" 
            fontWeight="500" 
            align="center"
            sx={{ color: theme.palette.text.primary }}
          >
            {t("Ask anything about Shoeb's Portfolio?")}
          </Typography>
          
          <Box 
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              width: "100%",
            }}
          >
            {randomSuggestions.map((question, index) => (
              <Paper
                key={index}
                elevation={0}
                onClick={() => handleSuggestedQuestion(question)}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'transparent',
                  cursor: "pointer",
                  transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], {
                    duration: theme.transitions.duration.standard,
                  }),
                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[2],
                  },
                }}
              >
                <Typography variant="body1">{question}</Typography>
              </Paper>
            ))}
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    <Container 
      maxWidth="md"
      disableGutters
      sx={{ 
        display: "flex", 
        flexDirection: "column",
        position: "relative",
        flexGrow: 1,
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgb(52, 53, 65)' 
          : 'rgb(255, 255, 255)',
        overflow: "hidden",
      }}
    >
      {/* Error message */}
      <Collapse in={!!error}>
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ 
            m: 2,
            position: "fixed",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            maxWidth: "90%",
          }}
        >
          {error}
        </Alert>
      </Collapse>

      {/* Chat messages container */}
      <Box
        ref={messageContainerRef}
        sx={{
          flexGrow: 1,
          overflowY: initialLoad ? "hidden" : "auto",
          pb: "110px", // Space for the input at bottom
          display: "flex", 
          flexDirection: "column",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.2)' 
              : 'rgba(0, 0, 0, 0.2)',
            borderRadius: "4px",
          },
          scrollBehavior: "smooth",
        }}
        role="log"
        aria-live="polite"
      >
        {initialLoad && !hasUserSentMessage ? (
          <InitialScreen />
        ) : (
          <>
            {/* Start with some padding to prevent cutting off the top of first message */}
            <Box pt={2} />
            
            {/* Render all chat messages */}
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                messageId={message.id}
                message={message.content}
                isUser={message.isUser}
                timestamp={message.timestamp}
                onTypingStateChange={!message.isUser ? handleTypingStateChange : undefined}
              />
            ))}

            {/* Show thinking loader when waiting for response */}
            {isLoading && lastMessageIsUser && (
              <ThinkingLoader />
            )}

            {/* Show suggested questions after assistant messages or at start */}
            {(!lastMessageIsUser || messages.length <= 1) && !isLoading && !initialLoad && !isCurrentlyTyping && (
              <SuggestedQuestions />
            )}
          </>
        )}

        {/* Invisible element to scroll to */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Chat input area - fixed at bottom */}
      <ChatInput 
        onSendMessage={handleSendMessage} 
        disabled={isLoading}
      />
    </Container>
  );
};

export default ChatInterface;